/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  layerSelectors,
  layerActions,
  layerUtils,
  serviceSelectors,
  uiTypes,
  useSetupDialog,
  layerTypes,
} from '@opengeoweb/store';
import { LayerType } from '@opengeoweb/webmap';
import { LayerInfoDialog } from '@opengeoweb/webmap-react';

interface LayerInfoDialogConnectProps {
  source: uiTypes.Source;
  mapId: string;
}

const hasSameServiceUrl = (urlA: string, urlB: string): boolean =>
  layerUtils.trimTrailingSlashes(urlA) === layerUtils.trimTrailingSlashes(urlB);

const hasSameSTACServiceUrl = (urlA: string, urlB: string): boolean =>
  hasSameServiceUrl(
    layerUtils.getSTACServiceRootUrl(urlA),
    layerUtils.getSTACServiceRootUrl(urlB),
  );

const isSTACCollectionUrl = (serviceUrl: string): boolean =>
  layerUtils.trimTrailingSlashes(serviceUrl) !==
  layerUtils.getSTACServiceRootUrl(serviceUrl);

const hasMatchingSTACLayerServiceUrl = (
  layerServiceUrl: string,
  layerInfoServiceUrl: string,
): boolean => {
  if (hasSameServiceUrl(layerServiceUrl, layerInfoServiceUrl)) {
    return true;
  }

  return (
    !isSTACCollectionUrl(layerInfoServiceUrl) &&
    hasSameSTACServiceUrl(layerServiceUrl, layerInfoServiceUrl)
  );
};

export const LayerInfoDialogConnect: React.FC<LayerInfoDialogConnectProps> = ({
  source,
  mapId,
}) => {
  const dispatch = useDispatch();
  const layerInfo = useSelector((store: CoreAppStore) =>
    layerSelectors.getActiveLayerInfo(store),
  );

  const selectedMapLayer = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayersByMapId(store, mapId).find((layer) => {
      if (!layerInfo || layer.name !== layerInfo.layerName) {
        return false;
      }

      if (layer.service === layerInfo.serviceUrl) {
        return true;
      }

      if (layer.layerType !== LayerType.stacGeoTIFFLayer || !layer.service) {
        return false;
      }

      return hasMatchingSTACLayerServiceUrl(
        layer.service,
        layerInfo.serviceUrl,
      );
    }),
  );

  const layerInfoServiceUrl = layerInfo?.serviceUrl ?? '';
  const isSelectedSTACLayer =
    selectedMapLayer?.layerType === LayerType.stacGeoTIFFLayer;
  const serviceLookupUrl = isSelectedSTACLayer
    ? layerUtils.getSTACServiceRootUrl(
        selectedMapLayer.service ?? layerInfoServiceUrl,
      )
    : layerInfoServiceUrl;

  const currentService = useSelector((store: CoreAppStore) => {
    if (!isSelectedSTACLayer) {
      return serviceSelectors.getServiceByUrl(store, serviceLookupUrl);
    }

    return Object.values(serviceSelectors.getServices(store)).find(
      (service) =>
        service.serviceUrl &&
        hasSameServiceUrl(service.serviceUrl, serviceLookupUrl),
    );
  });

  const layer = currentService?.layers?.find(
    (serviceLayer) => serviceLayer.name === (layerInfo?.layerName || ''),
  );

  /* Get layerDetails with retrieved getLayerId */
  const layerDetails = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerById(store, selectedMapLayer?.id || ''),
  );

  /* Use layerDetails to toggle legend visibility */
  const onToggleLayerLegendVisibility = (showLayerLegend: boolean): void => {
    const layerId = layerDetails?.id || '';
    dispatch(
      layerActions.SetLayerLegendVisibilityPayload({
        layerId,
        showLayerLegend,
        mapId,
        origin: layerTypes.LayerActionOrigin.layerManager,
      }),
    );
  };

  /* Use layerDetails for showLegend value */
  const showLayerLegend =
    typeof layerDetails?.showLayerLegend === 'boolean'
      ? layerDetails.showLayerLegend
      : true;

  const { dialogOrder, onCloseDialog, setDialogOrder, isDialogOpen } =
    useSetupDialog(uiTypes.DialogTypes.LayerInfo);
  if (!layer) {
    return null;
  }
  return (
    <LayerInfoDialog
      edrParameterMetadata={
        selectedMapLayer?.layerType === LayerType.edrFeatureLayer
          ? (
              selectedMapLayer.options as {
                parameterMetadata?: {
                  id?: string;
                  label?: string;
                  description?: string;
                  observedProperty?: { label?: string };
                  unit?: { label?: string; symbol?: { value?: string } };
                };
              }
            )?.parameterMetadata
          : undefined
      }
      isOpen={isDialogOpen}
      layer={layer}
      onClose={onCloseDialog}
      onMouseDown={setDialogOrder}
      onToggleLayerLegendVisibility={onToggleLayerLegendVisibility}
      order={dialogOrder}
      selectedStyle={layerInfo?.style}
      serviceName={currentService?.name!}
      serviceUrl={currentService?.serviceUrl}
      showLayerLegend={showLayerLegend}
      source={source}
    />
  );
};
